import { useQuery, useQueryClient } from '@tanstack/react-query';
import { userRoleSchema, userStatusSchema, type UserDto, type UserRole, type UserStatus } from 'chery-contracts';
import { Copy, Plus } from 'lucide-react';
import { useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Notice, type NoticeTone } from '../../components/ui/Notice';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TableCell, TableHead, TableHeaderCell, TableRow } from '../../components/ui/Table';
import { cn } from '../../lib/cn';
import { useDebouncedValue } from '../../lib/use-debounced-value';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { accessSummary, formatSignIn, isLocked } from './user-display';
import { UserSheet, type TemporaryPasswordResult } from './UserSheet';

interface PageNotice {
  tone: NoticeTone;
  text: string;
}

type SheetState = { user?: UserDto } | null;

/** The one-time temporary password, shown once on the page and gone for good when dismissed or reloaded. */
const TemporaryPasswordBanner = ({ result, onDismiss }: { result: TemporaryPasswordResult; onDismiss: () => void }) => {
  const { t } = useI18nTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(result.password);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Notice
      tone="success"
      title={t('users.tempTitle', { email: result.email })}
      onDismiss={onDismiss}
    >
      <div className="flex flex-wrap items-center gap-3">
        <code
          data-testid="temporary-password"
          className="rounded bg-background px-2 py-1 font-mono text-base tracking-wider text-foreground"
        >
          {result.password}
        </code>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void copy()}
        >
          <Copy className="size-3.5" />
          {copied ? t('actions.copied') : t('actions.copy')}
        </Button>
      </div>
      <p className="text-xs">
        {t('users.tempBody')} {result.mustChange ? t('users.tempMustChange') : t('users.tempNoChange')}
      </p>
    </Notice>
  );
};

/** Users (plan §7) — ADMIN only, behind `RequireRole` here and `@Roles('ADMIN')` on every `users.*` call. */
export const UsersPage = () => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [sheet, setSheet] = useState<SheetState>(null);
  const [notice, setNotice] = useState<PageNotice | null>(null);
  const [temporary, setTemporary] = useState<TemporaryPasswordResult | null>(null);

  const debouncedSearch = useDebouncedValue(search.trim());
  const usersQuery = useQuery(
    orpc.users.list.queryOptions({ input: { search: debouncedSearch || undefined, role: role || undefined, status: status || undefined } }),
  );
  const users = usersQuery.data?.items ?? [];
  const now = new Date();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.users.key() });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('pages.users')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('users.subtitle')}</p>
        </div>
        <Button onClick={() => setSheet({})}>
          <Plus className="size-4" />
          {t('users.new')}
        </Button>
      </div>

      {temporary ? (
        <TemporaryPasswordBanner
          result={temporary}
          onDismiss={() => setTemporary(null)}
        />
      ) : null}

      {notice ? (
        <Notice
          tone={notice.tone}
          onDismiss={() => setNotice(null)}
        >
          {notice.text}
        </Notice>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('users.search')}
          aria-label={t('users.search')}
          className="w-64"
        />
        <Select
          value={role}
          onChange={(event) => setRole(event.target.value as UserRole | '')}
          options={[{ value: '', label: t('users.allRoles') }, ...userRoleSchema.options.map((value) => ({ value, label: t(`roles.${value}`) }))]}
          aria-label={t('users.columns.role')}
          className="w-52"
        />
        <Select
          value={status}
          onChange={(event) => setStatus(event.target.value as UserStatus | '')}
          options={[
            { value: '', label: t('users.allStatuses') },
            ...userStatusSchema.options.map((value) => ({ value, label: t(`enums.status.${value}`) })),
          ]}
          aria-label={t('users.columns.status')}
          className="w-40"
        />
      </div>

      <Table>
        <TableHead>
          <tr>
            <TableHeaderCell>{t('users.columns.name')}</TableHeaderCell>
            <TableHeaderCell>{t('users.columns.email')}</TableHeaderCell>
            <TableHeaderCell>{t('users.columns.role')}</TableHeaderCell>
            <TableHeaderCell>{t('users.columns.access')}</TableHeaderCell>
            <TableHeaderCell>{t('users.columns.status')}</TableHeaderCell>
            <TableHeaderCell>{t('users.columns.lastSignIn')}</TableHeaderCell>
          </tr>
        </TableHead>
        <tbody>
          {usersQuery.isLoading ? (
            <tr>
              <td
                colSpan={6}
                className="px-4 py-12 text-center text-muted-foreground"
              >
                <span className="inline-flex items-center gap-2">
                  <Spinner />
                  {t('app.loading')}
                </span>
              </td>
            </tr>
          ) : users.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                className="px-4 py-12 text-center text-muted-foreground"
              >
                {usersQuery.isError ? t('errors.loadFailed') : t('users.empty')}
              </td>
            </tr>
          ) : (
            users.map((user) => (
              <TableRow
                key={user.id}
                data-user-email={user.email}
                onClick={() => setSheet({ user })}
                className={cn('cursor-pointer transition-colors hover:bg-muted/60', user.status === 'INACTIVE' && 'text-muted-foreground')}
              >
                <TableCell className="font-medium">{user.name}</TableCell>
                <TableCell className="text-muted-foreground">{user.email}</TableCell>
                <TableCell className="whitespace-nowrap">{t(`roles.${user.role}`)}</TableCell>
                <TableCell className="text-xs">{accessSummary(user)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {user.status === 'INACTIVE' ? (
                      <Badge tone="outline">{t('enums.status.INACTIVE')}</Badge>
                    ) : (
                      <span className="text-xs">{t('enums.status.ACTIVE')}</span>
                    )}
                    {isLocked(user.lockedUntil, now) ? <Badge tone="danger">{t('users.locked')}</Badge> : null}
                    {user.mustChangePassword ? <Badge>{t('users.mustChange')}</Badge> : null}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatSignIn(user.lastLoginAt)}</TableCell>
              </TableRow>
            ))
          )}
        </tbody>
      </Table>

      <UserSheet
        isOpen={sheet !== null}
        user={sheet?.user}
        onClose={() => setSheet(null)}
        onSaved={(user, created) => {
          void invalidate();
          setSheet(null);
          setNotice({ tone: 'success', text: created ? t('users.created', { name: user.name }) : t('users.saved', { name: user.name }) });
        }}
        onTemporaryPassword={(result) => {
          setSheet(null);
          setTemporary(result);
        }}
        onSignedOut={(user, revoked) => {
          setSheet(null);
          setNotice({ tone: 'success', text: t('users.signedOut', { name: user.name, count: revoked }) });
        }}
      />
    </div>
  );
};
